import React, { Suspense, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Separator, Tab, Tabs, Row, LayoutBand } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import { ContentLock, ContentUnlock } from '@fs/zion-icon'
import { AuthRoute, Switch, Route, NotFound, Redirect, useLocation } from '@fs/zion-router'
import IsHelpingDispatcher from './components/shared/IsHelpingDispatcher'
import { RequestStatus } from './components/shared'
import { AccountLockedContext } from './context/AccountLockedProvider'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const ProfilePage2 = React.lazy(() => import('./components/profile/ProfilePage2'))
const AccountPage = React.lazy(() => import('./components/account/AccountPage'))
const NotificationsPage = React.lazy(() => import('./components/notifications/NotificationsPage'))
const PermissionsPage = React.lazy(() => import('./components/permissions/PermissionsPage'))
const TempleExperiencePage = React.lazy(() => import('./components/temple-experience/TempleExperiencePage'))
const ConnectProviderPage = React.lazy(() => import('./components/account/ConnectProviderPage'))

const SHOW_TABS_MAX_INDEX = 10

const paths = ['/', '/profile', '/account', '/notifications', '/permissions', '/temple-experience', '/connect-provider']
const [root, profile, account, notifications, permissions, templeExperience, connectProvider] = paths
const tabIndexByPath = {
  [root]: 0,
  [profile]: 0,
  [account]: 1,
  [notifications]: 2,
  [permissions]: 3,
  // use tab index above SHOW_TABS_MAX_INDEX to hide tabs
  [templeExperience]: 11,
}

// control tab index since we can have multiple paths per tab
function getTabIndex(location) {
  const tabIndex = tabIndexByPath[location.pathname]
  // use -1 for not found routes
  return typeof tabIndex === 'number' ? tabIndex : -1
}

function App() {
  const { t } = useTranslation()
  const accountLocked = useContext(AccountLockedContext)
  const location = useLocation()
  const tabIndex = getTabIndex(location)
  const accountTab = (
    <Row alignY="middle" alignX="center" gutters="nano">
      {tabIndex !== tabIndexByPath[account] || accountLocked ? <ContentLock size="xs" /> : <ContentUnlock size="xs" />}
      <span>{t('account.link', 'Account')}</span>
    </Row>
  )

  return (
    <ErrorBoundary>
      <LayoutBand maxWidth="xxs">
        {tabIndex < SHOW_TABS_MAX_INDEX && (
          <Tabs fullWidth index={tabIndex}>
            <Tab title={t('profile.link', 'Profile')} to={profile} linkName="profile" />
            <Tab title={accountTab} to={account} linkName="account" />
            <Tab title={t('notifications.link', 'Notifications')} to={notifications} linkName="notifications" />
            <Tab title={t('permissions.link', 'Permissions')} to={permissions} linkName="permissions" />
          </Tabs>
        )}
      </LayoutBand>
      <LayoutBand maxWidth="nano">
        <Separator size="md" />
        <Suspense fallback={<RequestStatus loading />}>
          <Switch>
            <Route exact path={root}>
              <Redirect to={profile} />
            </Route>
            {/* Use a single auth route so that we can more easily put providers inside */}
            <AuthRoute exact path={paths}>
              <IsHelpingDispatcher>
                <Route exact path={profile} component={ProfilePage2} />
                <Route exact path={account} component={AccountPage} />
                <Route exact path={notifications} component={NotificationsPage} />
                <Route exact path={permissions} component={PermissionsPage} />
              </IsHelpingDispatcher>
              <Route exact path={templeExperience} component={TempleExperiencePage} />
              <Route exact path={connectProvider} component={ConnectProviderPage} />
            </AuthRoute>
            {/* Legacy routes */}
            <Route path="/contact">
              <Redirect to={profile} />
            </Route>
            <Route path="/password">
              <Redirect to={account} />
            </Route>
            <Route path="/preferences">
              <Redirect to={account} />
            </Route>
            <Route path="/connected-accounts">
              <Redirect to={permissions} />
            </Route>

            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </LayoutBand>
    </ErrorBoundary>
  )
}
export default App
